import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import BrandShell from '../components/BrandShell.jsx'
import HelpTooltip from '../components/HelpTooltip.jsx'
import { ACTIVITY_TYPES, previewPoints } from '../lib/scoring.js'
import { extractUrn, isLinkedInUrl } from '../lib/linkedin.js'
import { supabase } from '../lib/supabase.js'
import { useParticipant } from '../hooks/useParticipant.js'
import { fireConfetti } from '../components/ConfettiBurst.jsx'

const MAX_POST_CHARS = 3000

async function authedFetch(path, session, body) {
  return fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  })
}

export default function LogActivity() {
  const navigate = useNavigate()
  const { participant, session } = useParticipant()
  const [type, setType] = useState('post')
  // For posts, default to the paste-link flow — most teammates write natively
  // on LinkedIn and just want to log what they already published. Compose &
  // publish via Publora is opt-in for people who want auto-tracked impressions.
  const [postMethod, setPostMethod] = useState('url')

  if (type === 'post') {
    return (
      <BrandShell>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
          <button onClick={() => navigate(-1)} className="text-sm text-brand-text-muted hover:text-brand-text mb-4">← Back</button>
          <TypeSelector type={type} setType={setType} />
          <PostMethodSelector method={postMethod} setMethod={setPostMethod} />
          {postMethod === 'url'
            ? <LogUrlActivity type={type} participant={participant} session={session} navigate={navigate} />
            : <ComposeAndPublish participant={participant} session={session} navigate={navigate} />}
        </div>
      </BrandShell>
    )
  }

  return (
    <BrandShell>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(-1)} className="text-sm text-brand-text-muted hover:text-brand-text mb-4">← Back</button>
        <TypeSelector type={type} setType={setType} />
        <LogUrlActivity type={type} participant={participant} session={session} navigate={navigate} />
      </div>
    </BrandShell>
  )
}

function PostMethodSelector({ method, setMethod }) {
  const options = [
    {
      id: 'url',
      label: 'Paste a link',
      hint: 'You already posted on LinkedIn — paste the URL.',
    },
    {
      id: 'compose',
      label: 'Write here & publish',
      hint: 'Compose in-app, publish via Publora, impressions auto-tracked.',
    },
  ]
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-card border border-brand-border p-5 sm:p-6 mb-4"
    >
      <span className="text-sm font-medium text-brand-text mb-2 inline-flex items-center gap-2">
        How do you want to log it?
      </span>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map(o => (
          <button
            key={o.id}
            type="button"
            onClick={() => setMethod(o.id)}
            className={`text-left px-4 py-3 rounded-lg border transition-all ${
              method === o.id
                ? 'border-brand-blue bg-brand-blue-tint text-brand-blue ring-2 ring-brand-blue-tint'
                : 'border-brand-border text-brand-text hover:border-brand-text-muted'
            }`}
          >
            <div className="text-sm font-medium">{o.label}</div>
            <div className="text-xs text-brand-text-muted mt-0.5">{o.hint}</div>
          </button>
        ))}
      </div>
    </motion.div>
  )
}

function TypeSelector({ type, setType }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-card border border-brand-border p-5 sm:p-6 mb-4"
    >
      <span className="text-sm font-medium text-brand-text mb-2 inline-flex items-center gap-2">
        What is it?
      </span>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {ACTIVITY_TYPES.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setType(t.id)}
            title={t.description}
            className={`px-3 py-3 rounded-lg border text-sm font-medium transition-all ${
              type === t.id
                ? 'border-brand-blue bg-brand-blue-tint text-brand-blue ring-2 ring-brand-blue-tint'
                : 'border-brand-border text-brand-text hover:border-brand-text-muted'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </motion.div>
  )
}

// ─── Original post: compose in-app, publish via Publora ─────────────────────
function ComposeAndPublish({ participant, session, navigate }) {
  const [content, setContent] = useState('')
  const [notes, setNotes] = useState('')
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState(null)
  const [progress, setProgress] = useState(null) // 'publishing' | 'waiting' | 'done'

  const preview = useMemo(() => previewPoints('post'), [])
  const chars = content.length
  const overLimit = chars > MAX_POST_CHARS
  const canSubmit = content.trim().length > 0 && !overLimit && !publishing

  const notConnected = participant && (!participant.publora_user_id || !participant.publora_platform_id)

  async function handlePublish(e) {
    e.preventDefault()
    if (!canSubmit || !session?.access_token) return
    setPublishing(true)
    setError(null)
    setProgress('publishing')
    try {
      const res = await authedFetch('/.netlify/functions/publora-publish', session, {
        content: content.trim(),
        notes: notes.trim() || null,
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.error || `Publish failed (${res.status})`)

      setProgress(json.status === 'published' ? 'done' : 'waiting')

      // Poll resolver up to ~30s so we can show confirmation before sending
      // them back. If it's still not published after that, the scheduled
      // resolver will pick it up within 2 minutes.
      if (json.status !== 'published' && json.activity_id) {
        for (let i = 0; i < 10; i++) {
          await new Promise(r => setTimeout(r, 3000))
          const r = await authedFetch('/.netlify/functions/publora-resolve-urn', session, {
            activity_id: json.activity_id,
          })
          const rj = await r.json().catch(() => ({}))
          if (rj?.status === 'published') { setProgress('done'); break }
          if (rj?.status === 'failed') throw new Error(`LinkedIn rejected the post: ${rj?.error || 'unknown error'}`)
        }
      }

      fireConfetti()
      navigate('/')
    } catch (err) {
      setError(err.message || 'Could not publish. Try again.')
      setProgress(null)
    } finally {
      setPublishing(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-card border border-brand-border p-6 sm:p-8"
    >
      <h1 className="text-2xl font-bold text-brand-text mb-1">Write your post</h1>
      <p className="text-sm text-brand-text-muted mb-6">
        Publishes to your LinkedIn via Publora so impressions track automatically.
      </p>

      {notConnected && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
          <div className="font-semibold text-amber-900 mb-1">Connect LinkedIn first</div>
          <p className="text-sm text-amber-900 mb-2">
            You haven't finished Publora's LinkedIn OAuth yet — that's how we publish on your behalf.
          </p>
          <button
            onClick={() => navigate('/activate/connect')}
            className="bg-brand-blue text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-brand-blue-hover"
          >
            Connect LinkedIn →
          </button>
        </div>
      )}

      <form onSubmit={handlePublish} className="space-y-5">
        <label className="block">
          <span className="text-sm font-medium text-brand-text inline-flex items-center gap-2">
            Post content
            <HelpTooltip>
              What you write here is exactly what appears on LinkedIn. Hashtags, emoji,
              line breaks all work. @mentions need LinkedIn's native picker though — for
              tagging people, paste the URL after you post natively.
            </HelpTooltip>
          </span>
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={10}
            required
            disabled={notConnected || publishing}
            placeholder="Write something worth reading. Climatise point of view, customer story, technical insight, data — anything that goes somewhere."
            className={`mt-1 w-full px-4 py-3 border rounded-lg text-sm leading-relaxed focus:outline-none focus:ring-2 resize-y min-h-[200px] ${
              overLimit
                ? 'border-brand-danger focus:ring-red-100'
                : 'border-brand-border focus:border-brand-blue focus:ring-brand-blue-tint'
            } ${notConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-brand-text-muted">
              Appears on LinkedIn exactly as typed. No edits, no templates.
            </span>
            <span className={`text-xs font-mono ${overLimit ? 'text-brand-danger font-semibold' : 'text-brand-text-muted'}`}>
              {chars}/{MAX_POST_CHARS}
            </span>
          </div>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-brand-text">Internal notes (optional)</span>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            disabled={publishing}
            placeholder="Context for Lennon — not shown on LinkedIn"
            className="mt-1 w-full px-4 py-2 border border-brand-border rounded-lg text-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue-tint resize-none"
          />
        </label>

        <div className="bg-brand-blue-tint border border-brand-blue/20 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wider text-brand-blue font-semibold mb-1">Point preview</div>
          <div className="text-2xl font-bold text-brand-text">+{preview.base} pts</div>
          <ul className="text-xs text-brand-text-muted mt-1 space-y-0.5">
            {preview.conditional.map(c => <li key={c}>{c}</li>)}
          </ul>
        </div>

        {progress && !error && (
          <div className="bg-brand-blue-tint/60 border border-brand-blue/20 rounded-lg px-4 py-3 text-sm text-brand-text">
            {progress === 'publishing' && 'Sending to Publora…'}
            {progress === 'waiting' && 'Queued. Waiting for LinkedIn to confirm the publish…'}
            {progress === 'done' && 'Live on LinkedIn. Analytics will flow in automatically.'}
          </div>
        )}

        {error && (
          <div className="text-sm text-brand-danger bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit || notConnected}
          className="w-full bg-brand-blue hover:bg-brand-blue-hover text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60"
        >
          {publishing ? 'Publishing…' : 'Publish to LinkedIn →'}
        </button>
      </form>
    </motion.div>
  )
}

// ─── Comments / reposts / inbounds: paste URL as before ─────────────────────
// We don't track impressions for these, so the activity-URN limitation
// doesn't bite. Keeps the existing proof flow for inbounds.
function LogUrlActivity({ type, participant, session, navigate }) {
  const [url, setUrl] = useState('')
  const [notes, setNotes] = useState('')
  const [screenshot, setScreenshot] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const isInbound = type === 'inbound'
  const isPost = type === 'post'
  const needsUrl = !isInbound
  // URN extraction is nice-to-have here (we don't fetch stats for comments or
  // reposts), so require only a LinkedIn hostname. This stops modern comment
  // URLs with dashCommentUrn / fsd_comment from being rejected because the
  // extractor couldn't make sense of them.
  const urlValid = !needsUrl || isLinkedInUrl(url)
  const preview = useMemo(() => previewPoints(type), [type])

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (needsUrl && !urlValid) return setError('That doesn\'t look like a LinkedIn URL.')
    if (isInbound && !screenshot) return setError('Inbound entries need a screenshot of the DM. Anonymise if sensitive.')
    setSubmitting(true)
    try {
      const urn = needsUrl ? (extractUrn(url)?.urn || null) : null
      let screenshotPath = null
      if (screenshot && supabase) {
        const path = `inbounds/${participant.id}/${Date.now()}-${screenshot.name}`
        const { error: upErr } = await supabase.storage.from('inbound-screenshots').upload(path, screenshot)
        if (upErr) throw upErr
        screenshotPath = path
      }

      const { data: inserted, error: insertErr } = await supabase
        .from('activities')
        .insert({
          participant_id: participant.id,
          type,
          url: needsUrl ? url.trim() : null,
          post_urn: urn,
          notes: notes.trim() || null,
          screenshot_path: screenshotPath,
          content_gate_passed: true,
          inbound_approved: isInbound ? false : null,
        })
        .select('id')
        .single()
      if (insertErr) throw insertErr

      authedFetch('/.netlify/functions/notify-activity-logged', session, {
        activity_id: inserted.id,
      }).catch(err => console.warn('notify-activity-logged failed:', err))

      fireConfetti()
      navigate('/')
    } catch (err) {
      setError(err.message || 'Could not save. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-card border border-brand-border p-6 sm:p-8"
    >
      <h1 className="text-2xl font-bold text-brand-text mb-1">Log {type}</h1>
      <p className="text-sm text-brand-text-muted mb-6">
        {isInbound
          ? 'A qualified DM or inbound conversation from one of your posts.'
          : isPost
            ? 'Paste the LinkedIn URL of the post you already published. Impressions can be self-reported later from your profile.'
            : 'Paste the LinkedIn URL. Comments and reposts don\'t earn impression bonuses.'}
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {needsUrl && (
          <label className="block">
            <span className="text-sm font-medium text-brand-text inline-flex items-center gap-2">
              LinkedIn URL
              <HelpTooltip>
                Click the three dots on the post/comment → "Copy link". Paste here.
              </HelpTooltip>
            </span>
            <input
              type="url"
              required
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://www.linkedin.com/..."
              className={`mt-1 w-full px-4 py-3 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 ${
                url && !urlValid
                  ? 'border-brand-danger focus:ring-red-100'
                  : 'border-brand-border focus:border-brand-blue focus:ring-brand-blue-tint'
              }`}
            />
            {url && !urlValid && (
              <span className="text-xs text-brand-danger mt-1 inline-block">
                That host isn't linkedin.com — double-check the URL.
              </span>
            )}
            {url && urlValid && (
              <span className="text-xs text-brand-success mt-1 inline-block">
                ✓ Got it.
              </span>
            )}
          </label>
        )}

        {isInbound && (
          <label className="block">
            <span className="text-sm font-medium text-brand-text inline-flex items-center gap-2">
              Screenshot of the DM
              <HelpTooltip>Proof so Lennon can verify. Anonymise sensitive details.</HelpTooltip>
            </span>
            <input
              type="file"
              accept="image/*"
              onChange={e => setScreenshot(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-brand-text file:bg-brand-blue file:text-white file:border-0 file:rounded-md file:px-3 file:py-1.5 file:font-semibold file:cursor-pointer file:hover:bg-brand-blue-hover"
            />
          </label>
        )}

        <label className="block">
          <span className="text-sm font-medium text-brand-text">Notes (optional)</span>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Anything Lennon should know"
            className="mt-1 w-full px-4 py-2 border border-brand-border rounded-lg text-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue-tint resize-none"
          />
        </label>

        <div className="bg-brand-blue-tint border border-brand-blue/20 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wider text-brand-blue font-semibold mb-1">Point preview</div>
          <div className="text-2xl font-bold text-brand-text">+{preview.base} pts</div>
          {isInbound && (
            <p className="text-xs text-brand-text-muted mt-2">Inbounds need approval before they count.</p>
          )}
        </div>

        {error && (
          <div className="text-sm text-brand-danger bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-brand-blue hover:bg-brand-blue-hover text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60"
        >
          {submitting ? 'Saving…' : 'Log it →'}
        </button>
      </form>
    </motion.div>
  )
}
